import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * 마우스로 드래그를 해서 스크롤할 수 있도록 지원해주는 훅입니다.
 * 참고: https://htmldom.dev/drag-to-scroll/
 */
export function useDragScroll(
  ref: React.RefObject<HTMLElement>,
  threshold = 0
): {
  isDragScrolling: boolean;
} {
  const currentPositionRef = useRef<{
    scrollLeft: number;
    cursorX: number;
  } | null>(null);
  const originalCursorRef = useRef<string | null>(null);
  const originalDraggableRef = useRef(false);
  const [isDragScrolling, setIsDragScrolling] = useState(false);

  const mouseMoveHandler = useCallback(
    (event: MouseEvent) => {
      if (ref.current && currentPositionRef.current) {
        const dx = event.clientX - currentPositionRef.current.cursorX;
        if (Math.abs(dx) > threshold) {
          setIsDragScrolling(true);
          // 마우스를 움직인 만큼 스크롤합니다.
          ref.current.scrollLeft = currentPositionRef.current.scrollLeft - dx;
        }
      }
    },
    [ref, threshold]
  );

  const mouseUpHandler = useCallback(() => {
    if (ref.current) {
      setIsDragScrolling(false);
      // 커서의 스타일이 명시적으로 정해져있지 않다면 originalCursorRef.current는 빈 문자열 값을 가지게 됩니다.
      if (originalCursorRef.current) {
        ref.current.style.cursor = originalCursorRef.current;
      } else {
        ref.current.style.removeProperty('cursor');
      }
      ref.current.style.removeProperty('user-select');

      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    }
  }, [ref, mouseMoveHandler]);

  const mouseDownHandler = useCallback(
    (event: MouseEvent) => {
      if (ref.current) {
        const currentObj = ref?.current;

        // 드래그하는 동안에는 커서를 grabbing으로 변경해줍니다.
        currentObj.style.cursor = 'grabbing';
        currentObj.style.userSelect = 'none';

        currentPositionRef.current = {
          scrollLeft: currentObj.scrollLeft,
          cursorX: event.clientX,
        };
        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
      }
    },
    [ref, mouseMoveHandler, mouseUpHandler]
  );

  useEffect(() => {
    const currentObj = ref?.current;
    const dragStartHandler = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (currentObj) {
      currentObj.addEventListener('mousedown', mouseDownHandler);
      originalCursorRef.current = currentObj.style.cursor;
      /**
       * 이미지 등의 요소들은 기본적으로 draggable의 디폴트 값이 true로 설정되어 있습니다.
       * 이 때문에, 기본 이미지 드래그 이벤트로 인해 Drag Scroll이 정상적으로 동작하지 않습니다.
       * 따라서, currentObj의 draggable 값을 true로 설정해준 후, dragstart 이벤트 발생 시
       * preventDefault 및 stopPropagation을 호출해서 기본 드래그 이벤트가 동작하지 않도록 해줍니다.
       */
      originalDraggableRef.current = currentObj.draggable;
      currentObj.draggable = true;
      currentObj.addEventListener('dragstart', dragStartHandler);
    }

    return () => {
      if (currentObj) {
        currentObj.removeEventListener('mousedown', mouseDownHandler);
        currentObj.removeEventListener('dragstart', dragStartHandler);
        currentObj.draggable = originalDraggableRef.current;
      }
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    };
  }, [ref, mouseDownHandler, mouseMoveHandler, mouseUpHandler]);

  return {isDragScrolling};
}
